import { Stack, Typography } from '@mui/joy';
import { t } from 'i18next';

import { useAuthUser } from 'auth/hooks';
import { Tooltip } from 'core/elements';
import {
  IPricingProfileAccessCustomer,
  IPricingProfileAccessCustomerLocation,
  IPricingProfileAccessItem,
} from 'pricing/types';
import { Color, Direction, Level, TooltipPosition } from 'theme/enums';

const MAX_CUSTOMERS = 2;
const MAX_LOCATIONS_PER_CUSTOMER = 2;

interface IProps {
  data: IPricingProfileAccessItem;
}

const formatLocation = (location: IPricingProfileAccessCustomerLocation) => {
  return location.customerAddressArNumber
    ? `${location.customerAddressName} [${location.customerAddressArNumber}]`
    : location.customerAddressName;
};

interface ICustomerEntryProps {
  customer: IPricingProfileAccessCustomer;
  maxLocations?: number;
}

const CustomerEntry = ({ customer, maxLocations }: ICustomerEntryProps) => {
  const customerLocations = customer.customerLocations ?? [];
  const locationsToRender =
    typeof maxLocations === 'number' ? customerLocations.slice(0, maxLocations) : customerLocations;

  return (
    <Stack gap={0}>
      <Typography level={Level.BODY_MD}>{customer.customerContactGroupName}</Typography>
      {locationsToRender.length === 0 && (
        <Typography color={Color.NEUTRAL} level={Level.BODY_SM} sx={{ pl: 2 }}>
          {t('labelAllLocations')}
        </Typography>
      )}
      {locationsToRender.map((location) => (
        <Typography
          key={location.pricingAccessCustomerLocationId}
          color={Color.NEUTRAL}
          level={Level.BODY_SM}
          sx={{ pl: 2 }}
        >
          {formatLocation(location)}
        </Typography>
      ))}
    </Stack>
  );
};

export const CustomersCell = ({ data }: IProps) => {
  const { user } = useAuthUser();

  if (!data) {
    return null;
  }

  if (data.allCustomers) {
    return <>{t('labelAll')}</>;
  }

  const isAlternateVersionEnabled = Boolean(user?.alternateVersionEnabled);
  const customers = data.customers ?? [];
  const visibleCustomers = customers.slice(0, MAX_CUSTOMERS);
  const totalLocations = customers.reduce(
    (sum, customer) => sum + (customer.customerLocations?.length ?? 0),
    0
  );
  const visibleLocations = visibleCustomers.reduce(
    (sum, customer) =>
      sum + Math.min(MAX_LOCATIONS_PER_CUSTOMER, customer.customerLocations?.length ?? 0),
    0
  );
  const hiddenLocationsCount = totalLocations - visibleLocations;
  const hiddenCustomersCount = Math.max(0, customers.length - MAX_CUSTOMERS);
  const hasOverflow = hiddenLocationsCount > 0 || hiddenCustomersCount > 0;
  const showLocationsLabel = hiddenLocationsCount > 0;
  const overflowCount = showLocationsLabel ? hiddenLocationsCount : hiddenCustomersCount;
  const moreLabelKey = showLocationsLabel
    ? isAlternateVersionEnabled
      ? 'labelMoreBillToCustomerLocations'
      : 'labelMoreCustomerLocations'
    : isAlternateVersionEnabled
    ? 'labelMoreBillToCustomers'
    : 'labelMoreCustomers';

  return (
    <Stack direction={Direction.COLUMN} gap={1}>
      {visibleCustomers.map((customer) => (
        <CustomerEntry
          key={customer.pricingAccessCustomerId}
          customer={customer}
          maxLocations={MAX_LOCATIONS_PER_CUSTOMER}
        />
      ))}

      {hasOverflow && (
        <Tooltip
          color={Color.PRIMARY}
          placement={TooltipPosition.BOTTOM_START}
          title={
            <Stack direction={Direction.COLUMN} gap={1}>
              {customers.map((customer) => (
                <CustomerEntry key={customer.pricingAccessCustomerId} customer={customer} />
              ))}
            </Stack>
          }
        >
          <Typography color={Color.PRIMARY} level={Level.BODY_SM} sx={{ cursor: 'pointer' }}>
            {t(moreLabelKey, { count: overflowCount })}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
};
